import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { PropertyImage } from '@/types';

interface UseImageUploadOptions {
  propertyId: string;
  onSuccess?: () => void;
  onOptimisticAdd?: (pendingImage: PendingImage) => void;
  onOptimisticRemove?: (tempId: string) => void;
  onOptimisticReplace?: (tempId: string, realImage: PropertyImage) => void;
}

export interface PendingImage {
  tempId: string;
  previewUrl: string;
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'done' | 'error';
  error?: string;
}

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif'] as const;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function useImageUpload({
  propertyId,
  onSuccess,
  onOptimisticAdd,
  onOptimisticRemove,
  onOptimisticReplace,
}: UseImageUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<PendingImage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const updateQueueItem = useCallback((tempId: string, updates: Partial<PendingImage>) => {
    setUploadQueue(prev => prev.map(item =>
      item.tempId === tempId ? { ...item, ...updates } : item
    ));
  }, []);

  const removeFromQueue = useCallback((tempId: string) => {
    setUploadQueue(prev => prev.filter(item => item.tempId !== tempId));
    onOptimisticRemove?.(tempId);
  }, [onOptimisticRemove]);

  const uploadSingleImage = async (file: File, tempId: string): Promise<PropertyImage | null> => {
    const fileExt = file.name.split('.').pop()?.toLowerCase();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Nicht angemeldet');

      updateQueueItem(tempId, { progress: 10, status: 'uploading' });

      // Eindeutiger Dateiname mit sicherer Extension
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const storagePath = `${propertyId}/${fileName}`;

      // Upload zu Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('property-images')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      updateQueueItem(tempId, { progress: 70, status: 'processing' });

      // Eintrag in property_images Tabelle
      const { data: imageRecord, error: dbError } = await supabase
        .from('property_images')
        .insert({
          property_id: propertyId,
          storage_path: storagePath,
          file_name: file.name,
          file_size: file.size,
          is_primary: false,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      updateQueueItem(tempId, { progress: 100, status: 'done' });

      // Ersetze optimistisches Bild mit echtem
      onOptimisticReplace?.(tempId, imageRecord);

      // Nach kurzer Verzögerung aus Queue entfernen
      setTimeout(() => removeFromQueue(tempId), 500);

      return imageRecord;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen';
      console.error('Upload error:', err);
      updateQueueItem(tempId, { status: 'error', error: message });
      return null;
    }
  };

  const validateFile = (file: File): string | null => {
    if (!file.type.startsWith('image/')) {
      return 'Nur Bilddateien erlaubt';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Maximale Dateigröße: 5MB';
    }
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !ALLOWED_EXTENSIONS.includes(fileExt as typeof ALLOWED_EXTENSIONS[number])) {
      return `Nur folgende Formate erlaubt: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    return null;
  };

  // Einzelnes Bild hochladen (für Abwärtskompatibilität)
  const uploadImage = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return null;
    }

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const previewUrl = URL.createObjectURL(file);

    const pendingImage: PendingImage = {
      tempId,
      previewUrl,
      fileName: file.name,
      progress: 0,
      status: 'uploading',
    };

    setUploadQueue(prev => [...prev, pendingImage]);
    onOptimisticAdd?.(pendingImage);
    setIsUploading(true);
    setError(null);

    const result = await uploadSingleImage(file, tempId);

    setIsUploading(false);
    if (result) {
      onSuccess?.();
    }

    return result;
  };

  // Mehrere Bilder parallel hochladen
  const uploadImages = async (files: File[]) => {
    const validFiles: { file: File; tempId: string; previewUrl: string }[] = [];

    for (const file of files) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        continue;
      }

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const previewUrl = URL.createObjectURL(file);
      validFiles.push({ file, tempId, previewUrl });
    }

    if (validFiles.length === 0) return [];

    // Alle zu Queue hinzufügen
    const pendingImages: PendingImage[] = validFiles.map(({ tempId, previewUrl, file }) => ({
      tempId,
      previewUrl,
      fileName: file.name,
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploadQueue(prev => [...prev, ...pendingImages]);
    pendingImages.forEach(img => onOptimisticAdd?.(img));
    setIsUploading(true);
    setError(null);

    // Parallel hochladen (max 3 gleichzeitig)
    const results: (PropertyImage | null)[] = [];
    const chunks = chunkArray(validFiles, 3);

    for (const chunk of chunks) {
      const chunkResults = await Promise.all(
        chunk.map(({ file, tempId }) => uploadSingleImage(file, tempId))
      );
      results.push(...chunkResults);
    }

    setIsUploading(false);

    const successfulUploads = results.filter(Boolean);
    if (successfulUploads.length > 0) {
      onSuccess?.();
    }

    return results;
  };

  const deleteImage = async (image: PropertyImage) => {
    try {
      // Aus Storage löschen
      const { error: storageError } = await supabase.storage
        .from('property-images')
        .remove([image.storage_path]);

      if (storageError) throw storageError;

      // Aus DB löschen
      const { error: dbError } = await supabase
        .from('property_images')
        .delete()
        .eq('id', image.id);

      if (dbError) throw dbError;

      onSuccess?.();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Löschen fehlgeschlagen';
      console.error('Delete error:', err);
      setError(message);
      return false;
    }
  };

  const setPrimaryImage = async (imageId: string) => {
    try {
      // Erst alle auf nicht-primär setzen
      await supabase
        .from('property_images')
        .update({ is_primary: false })
        .eq('property_id', propertyId);

      // Dann das gewählte Bild als primär setzen
      const { error } = await supabase
        .from('property_images')
        .update({ is_primary: true })
        .eq('id', imageId);

      if (error) throw error;

      onSuccess?.();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Setzen des Primärbildes';
      console.error('Set primary error:', err);
      setError(message);
      return false;
    }
  };

  return {
    uploadImage,
    uploadImages,
    deleteImage,
    setPrimaryImage,
    isUploading,
    uploadQueue,
    error,
    clearError: () => setError(null),
    removeFromQueue,
  };
}

// Helper: Array in Chunks aufteilen
function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

// Cache für signierte URLs (1 Stunde gültig)
const urlCache = new Map<string, { url: string; expiresAt: number }>();
const CACHE_DURATION = 55 * 60 * 1000; // 55 Minuten (etwas weniger als URL-Gültigkeit)

// Helper: Signierte URL für ein Bild holen (mit Cache)
export async function getImageUrl(storagePath: string): Promise<string | null> {
  // Cache prüfen
  const cached = urlCache.get(storagePath);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  const { data } = await supabase.storage
    .from('property-images')
    .createSignedUrl(storagePath, 3600);

  if (data?.signedUrl) {
    urlCache.set(storagePath, {
      url: data.signedUrl,
      expiresAt: Date.now() + CACHE_DURATION,
    });
  }

  return data?.signedUrl || null;
}

// Helper: URLs für mehrere Bilder (optimiert mit Batch-Abruf)
export async function getImageUrls(images: PropertyImage[]): Promise<PropertyImage[]> {
  if (images.length === 0) return [];

  // Prüfe welche URLs noch im Cache sind
  const now = Date.now();
  const needsFetch: PropertyImage[] = [];
  const fromCache: PropertyImage[] = [];

  for (const img of images) {
    const cached = urlCache.get(img.storage_path);
    if (cached && cached.expiresAt > now) {
      fromCache.push({ ...img, url: cached.url });
    } else {
      needsFetch.push(img);
    }
  }

  // Wenn alle gecached sind, sofort zurückgeben
  if (needsFetch.length === 0) {
    return fromCache;
  }

  // Batch-Abruf für nicht-gecachte URLs
  const paths = needsFetch.map(img => img.storage_path);
  const { data } = await supabase.storage
    .from('property-images')
    .createSignedUrls(paths, 3600);

  // Ergebnisse verarbeiten und cachen
  const fetchedWithUrls: PropertyImage[] = [];
  if (data) {
    for (let i = 0; i < data.length; i++) {
      const signedUrl = data[i]?.signedUrl;
      if (signedUrl) {
        urlCache.set(needsFetch[i].storage_path, {
          url: signedUrl,
          expiresAt: now + CACHE_DURATION,
        });
        fetchedWithUrls.push({ ...needsFetch[i], url: signedUrl });
      }
    }
  }

  // Kombiniere Cache + frisch abgerufene, sortiert nach ursprünglicher Reihenfolge
  const allWithUrls = [...fromCache, ...fetchedWithUrls];
  const imageOrder = new Map(images.map((img, idx) => [img.id, idx]));
  allWithUrls.sort((a, b) => (imageOrder.get(a.id) ?? 0) - (imageOrder.get(b.id) ?? 0));

  return allWithUrls;
}

// Cache leeren (z.B. nach Logout)
export function clearImageUrlCache() {
  urlCache.clear();
}
